import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { sites, settings } from '../lib/api';

interface Site {
  id: string;
  name: string;
  subdomain: string;
  isPrimary: boolean;
  published: boolean;
}

interface SiteContextType {
  selectedSite: Site | null;
  sites: Site[];
  isLoading: boolean;
  setSelectedSite: (site: Site) => void;
  refreshSites: () => void;
}

const SiteContext = createContext<SiteContextType | undefined>(undefined);

export function SiteProvider({ children }: { children: ReactNode }) {
  const [selectedSite, setSelectedSiteState] = useState<Site | null>(null);

  // Fetch all sites
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['sites'],
    queryFn: () => sites.list(),
    refetchOnWindowFocus: false,
  });

  const sitesList: Site[] = data?.sites || [];

  // Initialize selected site from localStorage or use primary site
  useEffect(() => {
    if (sitesList.length > 0 && !selectedSite) {
      const savedSiteId = settings.getSiteId();

      let siteToSelect: Site | null = null;

      // Try to use saved site
      if (savedSiteId) {
        siteToSelect = sitesList.find((s: Site) => s.id === savedSiteId) || null;
      }

      // Fall back to primary site
      if (!siteToSelect) {
        siteToSelect = sitesList.find((s: Site) => s.isPrimary) || sitesList[0];
      }

      if (siteToSelect) {
        setSelectedSiteState(siteToSelect);
        settings.setSiteId(siteToSelect.id);
        // Also update subdomain for backwards compatibility
        settings.setSubdomain(siteToSelect.subdomain);
      }
    }
  }, [sitesList, selectedSite]);

  const setSelectedSite = (site: Site) => {
    setSelectedSiteState(site);
    settings.setSiteId(site.id);
    settings.setSubdomain(site.subdomain);
  };

  const refreshSites = () => {
    refetch();
  };

  return (
    <SiteContext.Provider
      value={{
        selectedSite,
        sites: sitesList,
        isLoading,
        setSelectedSite,
        refreshSites,
      }}
    >
      {children}
    </SiteContext.Provider>
  );
}

export function useSite() {
  const context = useContext(SiteContext);
  if (context === undefined) {
    throw new Error('useSite must be used within a SiteProvider');
  }
  return context;
}
